// src/components/EmptyState.jsx
import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import {
  Inbox as InboxIcon,
  SearchOff as SearchOffIcon,
  AssignmentLate as AssignmentLateIcon,
  DevicesOther as DevicesOtherIcon,
  NotificationsNone as NotificationsNoneIcon,
  FolderOpen as FolderOpenIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

/**
 * Empty State Component
 * Displays a friendly message when no data is available with optional action buttons
 *
 * @param {string} type - Type of empty state (generic, search, tickets, devices, notifications, files)
 * @param {string} title - Custom title (optional)
 * @param {string} description - Custom description (optional)
 * @param {React.ReactNode} icon - Custom icon component (optional)
 * @param {Object} action - Primary action button config {label, onClick, icon}
 * @param {Object} secondaryAction - Secondary action button config {label, onClick, icon}
 * @param {React.ReactNode} children - Additional content to render below description
 */
const EmptyState = ({
  type = 'generic',
  title,
  description,
  icon,
  action,
  secondaryAction,
  children
}) => {
  // Predefined empty state configurations
  const presets = {
    generic: {
      icon: <InboxIcon sx={{ fontSize: 80, color: 'text.disabled' }} />,
      title: 'No data available',
      description: 'There is no data to display at the moment.'
    },
    search: {
      icon: <SearchOffIcon sx={{ fontSize: 80, color: 'text.disabled' }} />,
      title: 'No results found',
      description: 'Try adjusting your search terms or filters to find what you\'re looking for.'
    },
    tickets: {
      icon: <AssignmentLateIcon sx={{ fontSize: 80, color: 'text.disabled' }} />,
      title: 'No tickets yet',
      description: 'There are no tickets matching your criteria. Create your first ticket to get started.'
    },
    devices: {
      icon: <DevicesOtherIcon sx={{ fontSize: 80, color: 'text.disabled' }} />,
      title: 'No devices found',
      description: 'No devices are currently registered in this location. Add a device to begin monitoring.'
    },
    notifications: {
      icon: <NotificationsNoneIcon sx={{ fontSize: 80, color: 'text.disabled' }} />,
      title: 'All caught up!',
      description: 'You have no new notifications. Check back later for updates.'
    },
    files: {
      icon: <FolderOpenIcon sx={{ fontSize: 80, color: 'text.disabled' }} />,
      title: 'No files yet',
      description: 'This folder is empty. Upload your first file to get started.'
    }
  };

  const preset = presets[type] || presets.generic;
  const displayIcon = icon || preset.icon;
  const displayTitle = title || preset.title;
  const displayDescription = description || preset.description;

  return (
    <Paper
      sx={{
        p: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minHeight: 300,
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(0, 0, 0, 0.01)'
      }}
    >
      <Box
        sx={{
          mb: 3,
          opacity: 0.5,
          animation: 'fadeIn 0.5s ease-in-out'
        }}
      >
        {displayIcon}
      </Box>

      <Typography
        variant="h6"
        color="text.primary"
        gutterBottom
        sx={{ fontWeight: 600, mb: 1 }}
      >
        {displayTitle}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, maxWidth: 500 }}
      >
        {displayDescription}
      </Typography>

      {children}

      {(action || secondaryAction) && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          {action && (
            <Button
              variant="contained"
              startIcon={action.icon || <AddIcon />}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="outlined"
              startIcon={secondaryAction.icon || <RefreshIcon />}
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
            >
              {secondaryAction.label}
            </Button>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default EmptyState;
